import { codeSetChanged, drgChanged, getDrgSaveMessage } from 'stores/grouper-store-helper';

const cloneDeep = require('lodash.clonedeep');
import { useQuasar } from 'quasar'
import { defineStore } from 'pinia';
import IodineGrouperService, { InitData } from 'src/services/IodineGrouperService';
import { GrouperService } from 'src/services/GrouperService';
import { computed, ref, ComputedRef, Ref } from 'vue';
import { GrouperData } from 'src/contracts/grouperData';
import { addDiagnosisCodes } from 'src/common/codeUtil';
import { Subscription } from 'rxjs';
import { Services } from 'src/services/service-manager';
import { DischargeStatus } from 'src/contracts/discharge-status';
import { isEmpty } from 'src/common/arrayUtil';
import { useLayoutStore } from 'stores/layout-store';
import { CalculateDrgRequestDto } from 'src/contracts/calculateDrgRequestDto';
import { DrgResponseDto } from 'src/contracts/drgResponseDto';
import { DiagnosisCode } from 'src/contracts/diagnosisCode';
import { PatientData } from 'src/contracts/patientData';
import {GrouperInfo} from 'src/contracts/grouperInfo';
import { EditData } from 'src/contracts/edit';
import {useResourcesStore} from 'stores/resources-store';
import { queue } from 'async';
import { getDiagnosesFromCalculationResult, getProceduresFromCalculationResult } from 'src/common/code-helper';
import { showSuccessSnackBar } from 'src/services/snackbar-service';
import { GrouperState } from 'src/contracts/encoderState';
import { setEncoderState } from 'stores/state-helper';
import { Code } from 'src/contracts/code';
import { SubmitOption } from 'components/grouper-common/common-constants';

interface CalculationArgs {
  calculate_primary: boolean,
  calculate_secondary: boolean
}

export const useGrouperStore = defineStore('grouperstore', () => {

  const calculateQueue = queue((args: CalculationArgs, callback) => {
    calculateDrg(args).then(() => callback()).catch(callback);
  }, 1);

  const grouperInfo= ref<GrouperInfo>()
  const dualGrouping = ref<boolean>(false);
  const resequence = ref<boolean>(false);
  const diagnosisCodes = ref<DiagnosisCode[]>([]);
  const procedureCodes = ref<Code[]>([]);
  const getCodesCount = computed(() => diagnosisCodes.value.length + procedureCodes.value.length);
  const edits = ref<EditData[]>([]);
  const isPrimaryDrgMs = computed(() => grouperInfo?.value?.grouper_source?.grouper_type == 'CMS');
  const primaryDrg = ref<DrgResponseDto>({});
  const secondaryDrg = ref<DrgResponseDto>({});
  const $q = useQuasar()
  const submitDrgTypes = ref<SubmitOption[]>([]);

  const patient = ref<PatientData>();
  const dischargeStatuses = ref<DischargeStatus[]>([]);
  const getDischargeStatuses = computed(() => dischargeStatuses.value);
  const dischargeStatus = ref<DischargeStatus | undefined>();
  const aprCalculationAvailable = computed(() => diagnosisCodes.value.length > 0 && aprRecalculationNeeded.value);
  const calculatingApr = ref(false);
  const isCalculatingApr = computed(() => calculatingApr.value);
  const aprRecalculationNeeded = ref(false);
  const submittingDrgs = ref(false);
  const isSubmittingDrgs = computed(() => submittingDrgs.value);
  const canSubmitDrg = computed(() =>
    (drgChanged(oldPrimaryDrg.value, primaryDrg.value)
      || drgChanged(oldSecondaryDrg.value, secondaryDrg.value)
      || codeSetChanged(diagnosisCodes.value, oldDiagnoses.value)
      || codeSetChanged(procedureCodes.value, oldProcedures.value))
    && !aprCalcNeededForSubmit.value
    && !isSubmittingDrgs.value
  );

  const aprCalcNeededForSubmit: ComputedRef<boolean> = computed(() =>
    <boolean>(dualGrouping.value && aprRecalculationNeeded.value && (!isPrimaryDrgMs.value || !!secondaryDrg?.value?.drg)));

  const oldPrimaryDrg = ref<DrgResponseDto>({});
  const oldSecondaryDrg = ref<DrgResponseDto>({});
  const oldDiagnoses = ref<DiagnosisCode[]>([])
  const oldProcedures = ref<Code[]>([]);

  function resetDRGData() {
    diagnosisCodes.value = [];
    procedureCodes.value = [];
  }

  function recalculateNeeded() {

    aprRecalculationNeeded.value = true;
    // automatically recalc MS
    calculateQueue.push({
      calculate_primary: !!isPrimaryDrgMs?.value,
      calculate_secondary: !isPrimaryDrgMs?.value
    });
  }

  async function addDiagnosisCode(code: string, description: string, poa?: string, snackbar = false): Promise<void> {

    if (diagnosisCodes.value.find((dx) => dx.code.toLowerCase() == code.toLowerCase())) {
      return;
    }

    diagnosisCodes.value.push({
      pdx: false,
      drgModifier: [],
      present_on_admission: poa || undefined,
      quality: [],
      risk: [],
      id: diagnosisCodes.value.length,
      action: '',
      code,
      description
    });

    if (snackbar) {
      showCodeAddSnackbar(diagnosisCodes, code);
    }

    recalculateNeeded();
  }

  function showCodeAddSnackbar(codes: Ref<Code[]>, code: string) {

    showSuccessSnackBar(`${code} has been added!`, [{
      label: 'Undo',
      handler: () => {
        removeCode(codes, code);
      }
    }], 10000);
  }

  async function changeResequence() {
    resequence.value = !resequence.value
    await recalculateNeeded()

    $q.notify({
      message: resequenceNotificationText(),
      color: 'green-11',
      html: true,
      textColor: 'black',
      timeout: 2000
    })
  }

  function resequenceNotificationText() {
    const notificationText = resequence.value ? 'Auto Sequence has been enabled. You are no longer permitted to drag and drop codes.' : 'Auto Sequence has been disabled. You are now permitted to drag and drop codes.';
    return '<div style="display: flex; align-items: center;">' +
        '<svg style="margin-right: 8px;" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M11.6188 14.6187C11.2781 14.9594 10.7219 14.9594 10.3812 14.6187L8.38125 12.6187C8.04063 12.2781 8.04063 11.7219 8.38125 11.3812C8.72187 11.0406 9.27813 11.0406 9.61875 11.3812L11 12.7625L14.3813 9.38125C14.7219 9.04063 15.2781 9.04063 15.6187 9.38125C15.9594 9.72187 15.9594 10.2781 15.6187 10.6188L11.6188 14.6187ZM20 12C20 16.4187 16.4187 20 12 20C7.58125 20 4 16.4187 4 12C4 7.58125 7.58125 4 12 4C16.4187 4 20 7.58125 20 12ZM12 5.5C8.40938 5.5 5.5 8.40938 5.5 12C5.5 15.5906 8.40938 18.5 12 18.5C15.5906 18.5 18.5 15.5906 18.5 12C18.5 8.40938 15.5906 5.5 12 5.5Z" fill="#008950"/>\n' +
        '</svg>' +
        `${notificationText}
        </div>`;
  }

  async function addProcedureCode(code: string, description: string, snackbar = false): Promise<void> {

    if (procedureCodes.value.find((proc) => proc.code.toLowerCase() == code.toLowerCase())) {
      return;
    }

    procedureCodes.value?.push({
      action: '',
      code,
      id: procedureCodes.value?.length || 0,
      description
    });

    if (snackbar) {
      showCodeAddSnackbar(procedureCodes, code);
    }

    recalculateNeeded()
  }

  function removeCode(codes: Ref<Code[]>, code: string): void {
    if (!code) {
      return;
    }

    const index = codes.value.findIndex((c) => c.code.toLowerCase() == code.toLowerCase());
    if (index != -1) {
      codes.value.splice(index, 1);
      recalculateNeeded();
    }
  }

  async function calculateDrg(calcArgs: CalculationArgs): Promise<void> {
    const grouperData: CalculateDrgRequestDto = {
      diagnoses: diagnosisCodes.value,
      procedures: procedureCodes.value,
      calculate_primary: calcArgs.calculate_primary,
      calculate_secondary: calcArgs.calculate_secondary,
      resequence: resequence.value
    };

    const grouperService = Services.service(GrouperService);
    const drgResponseDtos = await grouperService.calculateDrg(grouperData);
    diagnosisCodes.value = getDiagnosesFromCalculationResult(drgResponseDtos);
    procedureCodes.value = getProceduresFromCalculationResult(drgResponseDtos);

    handleDrgEdits(drgResponseDtos);
    updateDrgData(drgResponseDtos, calcArgs.calculate_primary, calcArgs.calculate_secondary);

    await useResourcesStore().getMsDrgAnalysis();

    if (calcArgs.calculate_primary && calcArgs.calculate_secondary) {
      aprRecalculationNeeded.value = false;
    }
  }

  function calculateApr(): void {

    aprRecalculationNeeded.value = false;
    calculatingApr.value = true;
    calculateQueue.push({
      calculate_primary: !isPrimaryDrgMs?.value,
      calculate_secondary: !!isPrimaryDrgMs?.value
    }, (err) => {
      calculatingApr.value = false;
      if (err) {
        console.error(err);
        aprRecalculationNeeded.value = true;
      }
    });
  }

  async function saveDrg(): Promise<void> {

    const types = submitDrgTypes?.value?.map(x => x.label);
    if (isEmpty(types)) {
      return;
    }

    submittingDrgs.value = true;
    const drgs: DrgResponseDto[] = [cloneDeep(primaryDrg.value)];
    if (dualGrouping.value) {
      drgs.push(<DrgResponseDto>cloneDeep(secondaryDrg.value));
    }

    drgs.forEach(drg => drg.payload_ready = !!drg.drg);

    const dxCopy = cloneDeep(diagnosisCodes.value);
    const procCopy = cloneDeep(procedureCodes.value);
    const grouperData: any  = {
      diagnoses: dxCopy,
      procedures: (<Code[]>procCopy).map(procedure => {
        if (!procedure.position) {
          procedure.position = procedure.id;
        }
        return procedure;
      }),
      grouper_state: types.map(type => type.toUpperCase()),
      drg_info: drgs
    };

    const grouperService = Services.service(GrouperService);
    try {
      await grouperService.saveDrg(grouperData);

      oldPrimaryDrg.value = drgs[0];
      if (drgs.length == 2) {
        oldSecondaryDrg.value = drgs[1];
      }

      oldDiagnoses.value = dxCopy;
      oldProcedures.value = procCopy;

      showSuccessSnackBar(getDrgSaveMessage(drgs, types));
    } finally {
      submittingDrgs.value = false;
    }
  }

  let setVisitContextSubscription: Subscription | undefined;
  let addCodeSubscription: Subscription | undefined;

  function createAddCodeSubscription() {
    addCodeSubscription = IodineGrouperService.addCodes$.subscribe(
      async (addCodeData: GrouperData) => {

        if (!isEmpty(addCodeData?.diagnosisCodes)) {
          addDiagnosisCodes(diagnosisCodes.value, addCodeData.diagnosisCodes);
        }
        if (!isEmpty(addCodeData?.procedureCodes)) {
          addCodeData.procedureCodes?.forEach(procedure => {
            addProcedureCode(procedure.code, procedure.description || '');
          });
        }
        recalculateNeeded();
      }
    );
  }

  function createContextSubscription() {
    setVisitContextSubscription = IodineGrouperService.setVisitContext$.subscribe(
      async (initData: InitData) => {
        resetDRGData();
        useLayoutStore().changeMode(initData.mode, false);
        Services.setDefaultConfig(initData.endpoint, initData.visitId, initData.facilityCode);
        dualGrouping.value = initData.dualGrouping;

        const grouperService = Services.service(GrouperService);
        // load discharge
        if (isEmpty(dischargeStatuses.value)) {

          grouperService.getDischargeStatuses().then((values) => {
            dischargeStatuses.value = values;
          }).catch(err => {
            console.error(err);
          });
        }

        if (initData?.state) {
          setEncoderState(initData.state);
        } else {
          const response = await grouperService.getGrouperData();

          updateDrgData(response.drgInfo, true, true);
          // store last submitted drg's separately, so we can track changes
          oldPrimaryDrg.value = cloneDeep(primaryDrg.value);
          oldSecondaryDrg.value = cloneDeep(secondaryDrg.value);
          grouperInfo.value = response.grouperInfo;
          diagnosisCodes.value = response.diagnosisCodes || [];
          procedureCodes.value = response.procedureCodes || [];
          oldDiagnoses.value = cloneDeep(diagnosisCodes.value);
          oldProcedures.value = cloneDeep(procedureCodes.value);
          patient.value = response.patient;
        }

        useResourcesStore().getMsDrgAnalysis();
      }
    );
  }

  function handleDrgEdits(drgResponseDto: DrgResponseDto[] | undefined): void {
    // reset edits
    edits.value = [];
    // reset edits on codes
    diagnosisCodes?.value?.forEach(dx => {
      dx.edit = undefined;
    });
    procedureCodes?.value?.forEach(dx => {
      dx.edit = undefined;
    });


    drgResponseDto?.forEach(drg => {
      drg.edits?.edit.forEach(edit => {
        edits.value.push(edit);

        const index = parseInt(edit.position || '-1');
        switch (edit.content_type) {
          case 'diagnoses':
            const diagnosisCode = diagnosisCodes.value?.at(index);
            if (diagnosisCode) {
              diagnosisCode.edit = edit;
            }
            break;
          case 'procedures':
            const procedureCode = procedureCodes.value?.at(index);
            if (procedureCode) {
              procedureCode.edit = edit;
            }
            break;
          default:
            console.log(`Unexpected content type: ${edit.contentType}`);
            break;
            // throw new Error(`Unexpected content type: ${edit.contentType}`);
        }
      })
    })
  }

  function updateDrgData(drgResponseDto: DrgResponseDto[], primaryCalculated: boolean, secondaryCalculated: boolean): void {
    if (isEmpty(drgResponseDto)) {
      primaryDrg.value = {};
      secondaryDrg.value = {};
      return;
    }

    if (primaryCalculated) {
      primaryDrg.value = drgResponseDto.find(drg => drg?.priority == 'PRIMARY') || {};
    }

    if (secondaryCalculated) {
      secondaryDrg.value = drgResponseDto.find(drg => drg?.priority != 'PRIMARY') || {};
    }
  }

  function setAllPoaYes() {
    if (diagnosisCodes?.value?.some(dx => dx.present_on_admission != 'Y')) {
      diagnosisCodes?.value?.forEach(dxCode => {
        dxCode.present_on_admission = 'Y';
      });
      recalculateNeeded();
    }
  }

  function $onMount() {
    createContextSubscription();
    createAddCodeSubscription();
  }

  function $reset() {
    setVisitContextSubscription && setVisitContextSubscription.unsubscribe();
    addCodeSubscription && addCodeSubscription.unsubscribe();
  }

  function getGrouperState(): GrouperState {
    return {
      patient: <PatientData>patient.value,
      grouperInfo: <GrouperInfo>grouperInfo.value,
      dualGrouping: dualGrouping.value,
      resequence: resequence.value,
      diagnosisCodes: diagnosisCodes.value,
      procedureCodes: procedureCodes.value,
      edits: edits.value,
      primaryDrg: primaryDrg.value,
      secondaryDrg: secondaryDrg.value,
      dischargeStatus: <DischargeStatus>dischargeStatus.value,
      aprRecalculationNeeded: aprRecalculationNeeded.value,
      oldPrimaryDrg: <DrgResponseDto>oldPrimaryDrg.value,
      oldSecondaryDrg: <DrgResponseDto>oldSecondaryDrg.value,
      oldDiagnoses: oldDiagnoses.value,
      oldProcedures: oldProcedures.value,
      submitDrgTypes: submitDrgTypes.value
    };
  }

  function setGrouperState(grouperState: GrouperState): void {

    patient.value = grouperState?.patient;
    grouperInfo.value = grouperState?.grouperInfo;
    dualGrouping.value = grouperState?.dualGrouping;
    resequence.value = grouperState?.resequence;
    diagnosisCodes.value = grouperState?.diagnosisCodes;
    procedureCodes.value = grouperState?.procedureCodes
    edits.value = grouperState?.edits;
    primaryDrg.value = grouperState?.primaryDrg;
    secondaryDrg.value = grouperState?.secondaryDrg;
    dischargeStatus.value = grouperState?.dischargeStatus;
    aprRecalculationNeeded.value = grouperState?.aprRecalculationNeeded;
    oldPrimaryDrg.value = grouperState?.oldPrimaryDrg;
    oldSecondaryDrg.value = grouperState?.oldSecondaryDrg;
    oldDiagnoses.value = grouperState?.oldDiagnoses;
    oldProcedures.value = grouperState?.oldProcedures;
    submitDrgTypes.value = grouperState?.submitDrgTypes;
  }

  return {
    diagnosisCodes,
    getCodesCount,
    procedureCodes,
    edits,
    resequence,
    primaryDRG: primaryDrg,
    secondaryDRG: secondaryDrg,
    patient,
    aprCalculationAvailable,
    aprRecalculationNeeded,
    addDiagnosisCode,
    addProcedureCode,
    $reset,
    $onMount,
    setVisitContextSubscription,
    addCodeSubscription,
    calculateApr,
    saveDrg,
    changeResequence,
    getDischargeStatuses,
    dischargeStatus,
    isPrimaryDrgMs,
    recalculateNeeded,
    isCalculatingApr,
    setAllPoaYes,
    canSubmitDrg,
    aprCalcNeededForSubmit,
    getGrouperState,
    setGrouperState,
    submitDrgTypes
  }
})
